class SplitSizeVariantSelector extends VariantSelector {
  constructor() {
    super();
    this.property = 'size';
    this.value = false;
    this.displayValue = false;
    this.unavailableLabel = this.getAttribute('unavailable-label');
    this.defaultLabel = this.getAttribute('default-label');
    this.formId = this.getAttribute('form-id');
    this.variantFontFamily = this.getAttribute('variant-font-family');

    // Legacy (no `parts` data) split configuration — 2 dimensions only.
    this.delimiter = this.getAttribute('delimiter') || ' ';
    this.dim1Prefix = this.getAttribute('dim1-prefix') || '';
    this.dim2Prefix = this.getAttribute('dim2-prefix') || '';

    // Fallback labels, used only when the data doesn't supply a parts label.
    this.fallbackLabels = {
      dim1: this.getAttribute('dim1-label') || 'Waist',
      dim2: this.getAttribute('dim2-label') || 'Length',
      dim3: this.getAttribute('dim3-label') || '',
    };

    // Internal state
    this.dimValues = { dim1: null, dim2: null, dim3: null };
    this.activeDims = [];   // e.g. ['dim1', 'dim2'] or ['dim1', 'dim2', 'dim3'], detected from data
    this.parsedSizes = [];  // Array of { original, label, dims: { dim1: {value, display, dataLabel}, ... }, enabled, rank }
    this.sizeMap = {};      // Maps composite key (e.g. "34|30") -> original size code

    // Expose options for compatibility with main-product-utils.js
    this.options = [];

    // DOM references, keyed by dimension.
    this.dimWrappers = {
      dim1: this.querySelector('.product-split-size-selector__dim1'),
      dim2: this.querySelector('.product-split-size-selector__dim2'),
      dim3: this.querySelector('.product-split-size-selector__dim3'),
    };
    this.dimTooltips = {
      dim1: this.querySelector('.product-split-size-selector__dim1-tooltip'),
      dim2: this.querySelector('.product-split-size-selector__dim2-tooltip'),
      dim3: this.querySelector('.product-split-size-selector__dim3-tooltip'),
    };
    this.dimLabelEls = {
      dim1: this.querySelector('.product-split-size-selector__dim1-label'),
      dim2: this.querySelector('.product-split-size-selector__dim2-label'),
      dim3: this.querySelector('.product-split-size-selector__dim3-label'),
    };
    // The visible "Waist:"/"Length:" heading — updated at render() with the
    // data-driven label (parts.dimN.label) when the product's data supplies one.
    this.dimHeadingEls = {
      dim1: this.querySelector('.product-split-size-selector__dim1-tooltip strong'),
      dim2: this.querySelector('.product-split-size-selector__dim2-tooltip strong'),
      dim3: this.querySelector('.product-split-size-selector__dim3-tooltip strong'),
    };
    // Expose label for conflict prompt — points to dim1, other dims handled separately
    this.label = this.dimLabelEls.dim1;

    // Variant picker messaging
    this.unavailableTooltip = this.getAttribute('unavailable-tooltip') || null;
    this.unavailableTooltipMobile = this.getAttribute('unavailable-tooltip-mobile') || null;
    this.inlineMsg = null;
    this.enableTooltip = this.getAttribute('enable-tooltip') !== 'false';
    this.enableSnackbar = this.getAttribute('enable-snackbar') !== 'false';
    // Use the element itself as the delegation wrapper so all dim fieldsets are covered
    this.wrapper = this;
  }

  /**
   * Parse a legacy composite size value into two dimensions (used only when
   * a size option has no structured `parts` data).
   * e.g., "34 30" with no prefixes -> { dim1: "34", dim2: "30" }
   * e.g., "W34 L30" with dim1Prefix="W", dim2Prefix="L" -> { dim1: "34", dim2: "30" }
   * @param {string} sizeValue - The composite size string
   * @returns {{ dim1: string, dim2: string } | null} Parsed dimensions or null if unparseable
   */
  parseLegacySize(sizeValue) {
    const strValue = String(sizeValue);
    const parts = strValue.split(this.delimiter);
    if (parts.length < 2) return null;

    let dim1 = parts[0];
    let dim2 = parts.slice(1).join(this.delimiter);

    // Strip prefixes if configured
    if (this.dim1Prefix && dim1.startsWith(this.dim1Prefix)) {
      dim1 = dim1.slice(this.dim1Prefix.length);
    }
    if (this.dim2Prefix && dim2.startsWith(this.dim2Prefix)) {
      dim2 = dim2.slice(this.dim2Prefix.length);
    }

    return { dim1: dim1.trim(), dim2: dim2.trim() };
  }

  /**
   * Build a composite lookup key from an ordered list of dimension values.
   * @param {Array<string>} values
   * @returns {string}
   */
  compositeKey(values) {
    return values.join('|');
  }

  /**
   * Determine which dim keys (dim1/dim2/dim3) are present, in order.
   * @param {Set<string>} dimsSet
   * @returns {Array<string>}
   */
  dimOrder(dimsSet) {
    return ['dim1', 'dim2', 'dim3'].filter((key) => dimsSet.has(key));
  }

  /**
   * Get the display label for a dimension: prefer the data-driven label
   * supplied by variant_data.size.parts.dimN.label, fall back to the
   * block-configured label otherwise.
   * @param {string} dimension
   * @returns {string}
   */
  getDimLabel(dimension) {
    const fromData = this.parsedSizes
      .map((entry) => entry.dims[dimension]?.dataLabel)
      .find(Boolean);
    return fromData || this.fallbackLabels[dimension];
  }

  /**
   * Parse all size options and build internal data structures.
   * Prefers structured `parts` data (option.parts.dimN = { code, display, label }})
   * when present; falls back to legacy composite-string parsing (2 dims only)
   * otherwise.
   * @param {Array} sizeOptions - Size options from window.variants.getVariantData('size')
   */
  parseSizes(sizeOptions) {
    const dimsSet = new Set();

    const rawEntries = sizeOptions
      .map((option) => {
        let dims;
        if (option.parts && Object.keys(option.parts).length) {
          dims = {};
          Object.entries(option.parts).forEach(([key, part]) => {
            dims[key] = {
              value: part.code,
              display: part.display || part.code,
              dataLabel: part.label,
            };
          });
        } else {
          const parsed = this.parseLegacySize(option.value);
          if (!parsed) return null;
          dims = {
            dim1: { value: parsed.dim1, display: parsed.dim1 },
            dim2: { value: parsed.dim2, display: parsed.dim2 },
          };
        }

        Object.keys(dims).forEach((key) => dimsSet.add(key));
        return { option, dims };
      })
      .filter(Boolean);

    this.activeDims = this.dimOrder(dimsSet);
    this.parsedSizes = [];
    this.sizeMap = {};

    rawEntries.forEach(({ option, dims }) => {
      const entry = {
        original: option.value,
        label: option.label,
        dims,
        enabled: option.enabled,
        rank: option.rank,
      };
      this.parsedSizes.push(entry);

      const values = this.activeDims.map((key) => dims[key]?.value);
      if (values.every((v) => v !== undefined)) {
        this.sizeMap[this.compositeKey(values)] = option.value;
      }
    });
  }

  /**
   * Get unique sorted values for a dimension, with enabled state determined
   * by cross-filtering against every other currently-selected dimension.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   * @returns {Array<{ value: string, display: string, enabled: boolean, rank: * }>}
   */
  getDimensionValues(dimension) {
    const seen = new Map();

    this.parsedSizes.forEach((entry) => {
      const dim = entry.dims[dimension];
      if (!dim || seen.has(dim.value)) return;
      seen.set(dim.value, { value: dim.value, display: dim.display, enabled: false, rank: entry.rank });
    });

    this.parsedSizes.forEach((entry) => {
      if (!entry.enabled) return;
      const dim = entry.dims[dimension];
      if (!dim) return;

      const otherDimsMatch = this.activeDims.every((otherKey) => {
        if (otherKey === dimension) return true;
        const selectedValue = this.dimValues[otherKey];
        if (!selectedValue) return true;
        return entry.dims[otherKey]?.value === selectedValue;
      });

      if (otherDimsMatch) {
        seen.get(dim.value).enabled = true;
      }
    });

    // Sort by rank (derived from the first occurrence), then by value
    return Array.from(seen.values()).sort((a, b) => {
      if (a.rank !== b.rank) return a.rank - b.rank;
      const aNum = parseFloat(a.value);
      const bNum = parseFloat(b.value);
      if (!isNaN(aNum) && !isNaN(bNum)) return aNum - bNum;
      return a.value.localeCompare(b.value);
    });
  }

  render() {
    const sizeOptions = window.variants.getVariantData(this.property);
    this.parseSizes(sizeOptions);

    if (this.parsedSizes.length === 0) {
      Object.values(this.dimWrappers).forEach((wrapper) => wrapper.classList.add('hidden'));
      return;
    }

    this.activeDims.forEach((dimension) => this.renderDimension(dimension));

    // Hide any dim fieldsets that aren't active for this product (e.g. dim3
    // when this product only has 2 size dimensions).
    ['dim1', 'dim2', 'dim3']
      .filter((key) => !this.activeDims.includes(key))
      .forEach((key) => this.dimWrappers[key].classList.add('hidden'));

    // Collect all radio inputs for compatibility with main-product-utils.js
    this.options = this.querySelectorAll('input[type="radio"]');

    this.activeDims.forEach((dimension) => {
      this.dimLabelEls[dimension].innerText = this.defaultLabel;
      this.dimHeadingEls[dimension].textContent = `${this.getDimLabel(dimension)}:`;
    });

    // Inline message element (mobile only — hidden on desktop via CSS), placed
    // after the last active dimension's fieldset.
    const lastDim = this.activeDims[this.activeDims.length - 1];
    const inlineMsg = document.createElement('p');
    inlineMsg.className = 'varpicker-inline-message';
    inlineMsg.setAttribute('aria-live', 'polite');
    this.dimWrappers[lastDim].insertAdjacentElement('afterend', inlineMsg);
    this.inlineMsg = inlineMsg;

    this.setupVarPickerHover();
    this.setupVarPickerTapMessage();
  }

  /**
   * Render pill buttons for a single dimension.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   */
  renderDimension(dimension) {
    const values = this.getDimensionValues(dimension);
    const wrapper = this.dimWrappers[dimension];
    const name = `split-size-${dimension}-option`;
    let markup = '';

    values.forEach((option, index) => {
      const selectorID = `selector-split-size-${dimension}-${index}`;
      const formattedValue = option.value.replace(/"/g, '&quot;').replace(/'/g, '&apos;');
      // This may be extended later to support hard-unavailable which would be OOS items.
      const availClass = option.enabled ? '' : 'soft-unavailable';

      markup += `<input id="${selectorID}" type="radio" form="${this.formId}" name="${name}" value="${formattedValue}" class="p-small ${availClass}" selected="false" aria-disabled="${!option.enabled}" />
      <label for="${selectorID}" class="${this.variantFontFamily}">
        <span class="">${option.display}</span>
        <span class="visually-hidden sr-unavailable">${!option.enabled ? this.unavailableLabel : ''}</span>
        <svg><line x1="0" y1="98%" x2="100%" y2="2%"></line></svg>
      </label>`;
    });

    wrapper.insertAdjacentHTML('beforeend', markup);
    wrapper.classList.toggle('hidden', values.length === 0);

    const radios = wrapper.querySelectorAll('input[type="radio"]');
    const onChange = this.onDimChanged.bind(this, dimension);

    radios.forEach((element) => {
      element.addEventListener('click', () => {
        // Deselect if already selected
        if (element.value === this.dimValues[dimension]) {
          element.checked = false;
          this.clearDimension(dimension);
          return;
        }

        // Soft-unavailable: show snackbar and clear the other dimensions
        if (element.classList.contains('soft-unavailable')) {
          this.showVarPickerSnackbar(false);
          this.activeDims
            .filter((key) => key !== dimension)
            .forEach((key) => this.clearDimension(key));
        }

        onChange();
      });
    });
  }

  /**
   * Update enabled/disabled states for a dimension's pills.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   */
  updateDimensionStates(dimension) {
    const values = this.getDimensionValues(dimension);
    const wrapper = this.dimWrappers[dimension];
    const name = `split-size-${dimension}-option`;

    values.forEach((option) => {
      const formattedValue = option.value.replace(/"/g, '\\"').replace(/'/g, "\\'");
      const input = wrapper.querySelector(`input[name="${name}"][value="${formattedValue}"]`);
      if (!input) return;

      const label = input.nextElementSibling.querySelector('span.sr-unavailable');

      // Remove all availability classes before applying current state
      input.classList.remove('disabled', 'soft-unavailable');

      if (option.enabled) {
        label.textContent = '';
      } else {
        const availClass = 'soft-unavailable';
        input.classList.add(availClass);
        label.textContent = this.unavailableLabel;
      }
      input.setAttribute('aria-disabled', !option.enabled);
    });
  }

  /**
   * Handle a pill selection change for a given dimension.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   */
  onDimChanged(dimension) {
    const name = `split-size-${dimension}-option`;
    const selected = this.querySelector(`[name="${name}"]:checked`);
    if (!selected) return;

    this.dimValues[dimension] = selected.value;
    const otherDims = this.activeDims.filter((key) => key !== dimension);
    otherDims.forEach((key) => this.updateDimensionStates(key));
    this.updateDimLabel(dimension);
    otherDims.forEach((key) => this.autoSelectSingleDimension(key));
    this.trySetCompositeSize();
  }

  /**
   * Clear a dimension selection, reset the composite size, and update every
   * dimension's states.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   */
  clearDimension(dimension) {
    this.dimValues[dimension] = null;
    const hadCompositeSize = !!this.value;
    this.value = false;

    // Uncheck all radios in the cleared dimension and reset its label
    const name = `split-size-${dimension}-option`;
    this.querySelectorAll(`input[name="${name}"]`).forEach((r) => (r.checked = false));
    this.dimLabelEls[dimension].innerText = this.defaultLabel;

    // Remove the size filter so downstream selectors (e.g., condition) update
    if (hadCompositeSize) {
      window.variants.removeFilter(this.property);
      window.dispatchEvent(new CustomEvent('variants-updated', { detail: { property: this.property } }));
      window.dispatchEvent(new CustomEvent('price-update'));
    }

    // Re-parse sizes excluding the condition filter (condition is downstream of size selection)
    // and the size filter itself, so we show all sizes available for the current color.
    const filters = window.variants.filters.filter(
      (f) => f.property !== this.property && f.property !== 'condition'
    );
    this.parseSizes(window.variants.getVariantData(this.property, filters));

    // Update every dimension — cross-filtering state may have changed for any of them
    this.activeDims.forEach((key) => this.updateDimensionStates(key));
  }

  /**
   * If every active dimension is selected, look up the composite size and
   * set it on the Variants system.
   */
  trySetCompositeSize() {
    const values = this.activeDims.map((key) => this.dimValues[key]);
    if (values.some((v) => !v)) return;

    const key = this.compositeKey(values);
    const compositeValue = this.sizeMap[key];

    if (compositeValue) {
      this.value = compositeValue;
      window.variants.setVariantOption(this.property, this.value);
    }
  }

  setValue(value) {
    super.setValue(value);

    const entry = this.parsedSizes.find((e) => e.original === value);
    if (!entry) return;

    this.activeDims.forEach((dimension) => {
      const dim = entry.dims[dimension];
      if (!dim) return;

      this.dimValues[dimension] = dim.value;
      const formattedValue = dim.value.replace(/"/g, '\\"').replace(/'/g, "\\'");
      const radio = this.querySelector(`[name="split-size-${dimension}-option"][value="${formattedValue}"]`);
      if (radio) {
        radio.checked = true;
        this.updateDimLabel(dimension);
      }
    });

    this.activeDims.forEach((dimension) => this.updateDimensionStates(dimension));
  }

  updateOptions(event) {
    if (event.detail.property === this.property) return;

    // Re-parse sizes with updated availability from Variants system
    const sizeOptions = window.variants.getVariantData(this.property);
    this.parseSizes(sizeOptions);

    this.activeDims.forEach((dimension) => this.updateDimensionStates(dimension));

    // Auto-select dimensions that have only one enabled option
    this.activeDims.forEach((dimension) => this.autoSelectSingleDimension(dimension));
  }

  /**
   * Auto-select a dimension if it has exactly one enabled value and nothing is currently selected.
   * @param {string} dimension - 'dim1', 'dim2', or 'dim3'
   */
  autoSelectSingleDimension(dimension) {
    if (this.dimValues[dimension]) return;

    const values = this.getDimensionValues(dimension);
    const enabled = values.filter((v) => v.enabled);
    if (enabled.length !== 1) return;

    const value = enabled[0].value;
    const name = `split-size-${dimension}-option`;
    const formattedValue = value.replace(/"/g, '\\"').replace(/'/g, "\\'");
    const radio = this.querySelector(`input[name="${name}"][value="${formattedValue}"]`);
    if (!radio) return;

    radio.checked = true;
    this.dimValues[dimension] = value;
    this.activeDims
      .filter((key) => key !== dimension)
      .forEach((key) => this.updateDimensionStates(key));
    this.updateDimLabel(dimension);
    this.trySetCompositeSize();
  }

  deselect() {
    super.deselect();
    this.activeDims.forEach((dimension) => {
      this.dimValues[dimension] = null;
      this.dimLabelEls[dimension].innerText = this.defaultLabel;
    });
    // Uncheck all radios across every dimension
    this.querySelectorAll('input[type="radio"]').forEach((r) => (r.checked = false));
  }

  updateSelected(event) {
    if (event.detail.property === this.property) {
      this.setValue(event.detail.code);
    }
  }

  updateDimLabel(dimension) {
    const name = `split-size-${dimension}-option`;
    const selected = this.querySelector(`[name="${name}"]:checked`);
    const tooltip = this.dimTooltips[dimension];
    const labelEl = this.dimLabelEls[dimension];
    const dimLabel = this.getDimLabel(dimension);

    if (selected) {
      const displayValue = this.querySelector(`label[for="${selected.id}"] span:first-child`).textContent;
      tooltip.dataset.acsbTooltip = `${dimLabel}: ${displayValue}`;
      labelEl.textContent = displayValue;
    }
  }
}

customElements.define('split-size-variant-selector', SplitSizeVariantSelector);
